import { useEffect, useRef } from 'react';

/**
 * Focuses the first editable input element inside a container element.
 * Safely ignores buttons, close icons, hidden elements, file inputs, disabled and readonly fields.
 * Guaranteed to never crash or loop.
 *
 * @param {HTMLElement} container The container DOM element (e.g. Dialog Paper, Form element, etc.)
 * @returns {boolean} true if an element was focused or already active, false otherwise.
 */
export const focusFirstField = (container) => {
  if (!container || typeof container.querySelector !== 'function') {
    return false;
  }

  // If user has already focused an editable field in this container, never steal focus
  const active = document.activeElement;
  if (
    active &&
    container.contains(active) &&
    (active.tagName === 'INPUT' || active.tagName === 'TEXTAREA' || active.tagName === 'SELECT' || active.getAttribute('role') === 'combobox') &&
    active.type !== 'button' &&
    active.type !== 'submit' &&
    active.type !== 'reset'
  ) {
    return true;
  }

  // Preferred editable input selector
  const primarySelector = [
    'input:not([type="hidden"]):not([type="button"]):not([type="submit"]):not([type="reset"]):not([type="file"]):not([type="checkbox"]):not([type="radio"]):not([disabled]):not([readonly])',
    'textarea:not([disabled]):not([readonly])',
    'select:not([disabled]):not([readonly])',
    '[role="combobox"]:not([aria-disabled="true"])',
  ].join(', ');

  // Secondary fallback selector (includes checkbox, radio)
  const fallbackSelector = [
    'input:not([type="hidden"]):not([type="button"]):not([type="submit"]):not([type="reset"]):not([type="file"]):not([disabled]):not([readonly])',
  ].join(', ');

  // Look in the most specific form/content areas first
  const searchRoots = [
    container.querySelector('.MuiDialogContent-root form'),
    container.querySelector('.MuiDialogContent-root'),
    container.querySelector('form'),
    container.querySelector('.main-card-container'),
    container,
  ].filter(Boolean);

  const attemptFocus = () => {
    // Re-check current activeElement to ensure no user focus is overwritten
    const curActive = document.activeElement;
    if (
      curActive &&
      container.contains(curActive) &&
      (curActive.tagName === 'INPUT' || curActive.tagName === 'TEXTAREA' || curActive.tagName === 'SELECT' || curActive.getAttribute('role') === 'combobox') &&
      curActive.type !== 'button' &&
      curActive.type !== 'submit'
    ) {
      return true;
    }

    const tryCandidates = (selector) => {
      for (const root of searchRoots) {
        const candidates = root.querySelectorAll(selector);
        for (const el of candidates) {
          // Must be visible in the DOM
          if (el && (el.offsetParent !== null || (el.getClientRects && el.getClientRects().length > 0))) {
            try {
              if (document.activeElement !== el) {
                el.focus();
              }
              return true;
            } catch (e) {
              // Continue search safely
            }
          }
        }
      }
      return false;
    };

    if (tryCandidates(primarySelector)) return true;
    if (tryCandidates(fallbackSelector)) return true;

    return false;
  };

  // Immediate attempt
  if (attemptFocus()) return true;

  // Single safe fallback attempt after DOM animation settles
  setTimeout(() => {
    attemptFocus();
  }, 60);

  return false;
};

/**
 * Custom React Hook to autofocus the first input inside a referenced container on mount or dependency changes.
 * Guarded against infinite re-renders and memory leaks.
 *
 * @param {Array} deps Dependencies array to trigger refocus (default [])
 * @returns {React.RefObject} Ref to attach to the form or container element
 */
export const useAutoFocusFirstField = (deps = []) => {
  const containerRef = useRef(null);
  const hasFocusedRef = useRef(false);
  const prevDepsRef = useRef(deps);

  // If dependencies changed (e.g. switching between Add and Edit item), reset focus lock
  const depsChanged = deps.some((dep, i) => !Object.is(dep, prevDepsRef.current[i]));
  if (depsChanged) {
    prevDepsRef.current = deps;
    hasFocusedRef.current = false;
  }

  useEffect(() => {
    if (hasFocusedRef.current) return;

    let isMounted = true;
    const timer = setTimeout(() => {
      if (isMounted && containerRef.current && !hasFocusedRef.current) {
        const success = focusFirstField(containerRef.current);
        if (success) {
          hasFocusedRef.current = true;
        }
      }
    }, 60);

    return () => {
      isMounted = false;
      clearTimeout(timer);
    };
  }, deps); // eslint-disable-line react-hooks/exhaustive-deps

  return containerRef;
};

